import React from 'react'
import Draggable from 'react-draggable'
import { Button, Divider, makeStyles, shorthands, tokens } from '@fluentui/react-components'
import { Title } from '../text/Title'
import { Dismiss24Regular} from '@fluentui/react-icons'
import { ModalActions } from './Modal'

const useStyles = makeStyles({
   backdrop: {
      top: 0,
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'default',
   },
   dialog: {
      backgroundColor: tokens.colorNeutralBackground1,
      borderRadius: tokens.borderRadiusXLarge,
      boxShadow: tokens.shadow64,
      position: 'absolute',
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '600px',
      maxHeight: '90dvh',
      '@media (max-width: 600px)': {
         width: '100vw',
         minWidth: '100vw',
      },
   },
   header: {
      ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalXXL),
   },
   content: {
      ...shorthands.padding(tokens.spacingVerticalXS, tokens.spacingHorizontalXXL),
      overflowY: 'auto',
      flex: 1,
      paddingTop: tokens.spacingVerticalM,
      marginBottom: tokens.spacingVerticalXXXL,
   },
   divider: {
      ...shorthands.padding(0, tokens.spacingHorizontalXXL),
   },
   hidden: {
      display: 'none',
   },
   titleButtonContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'grab',
      marginRight: tokens.spacingHorizontalS,
   },
   closeButton: {
      ...shorthands.padding(0, tokens.spacingHorizontalXXL),
   },
   action: {
      paddingBottom: tokens.spacingVerticalXXL,
      paddingLeft: tokens.spacingHorizontalXXL,
      paddingRight: tokens.spacingHorizontalXXL,
   },
})

type DraggableModalProps = {
   open: boolean
   title: string
   children: React.ReactNode
   onClose?: () => void
   action?: JSX.Element | JSX.Element[]
   noDivider?: boolean
   noCloseButton?: boolean
   secondaryButton?: () => JSX.Element
}

export const DraggableModal = ({ open, title, children, onClose, action, noDivider, noCloseButton, secondaryButton }: DraggableModalProps) => {
   const classes = useStyles()
   if (!open) return null

   return (
      <article className={classes.backdrop} onClick={(e) => e.stopPropagation()} role="dialog">
         <Draggable handle=".draggable-dialog-handle" bounds="parent">
            <div className={classes.dialog} onClick={(e) => e.stopPropagation()}>
               <section className={`${classes.titleButtonContainer} draggable-dialog-handle`}>
                  <div className={`${classes.header} draggable-dialog-handle`}>
                     <Title as="h3">{title}</Title>
                  </div>
                  <div>
                     {secondaryButton && secondaryButton()}
                     <Button
                        className={noCloseButton ? classes.hidden : classes.closeButton}
                        aria-label="Close"
                        appearance="transparent"
                        icon={<Dismiss24Regular />}
                        onClick={onClose}
                     />
                  </div>
               </section>
               <Divider className={noDivider ? classes.hidden : classes.divider} appearance="brand" />

               <div className={classes.content}>{children}</div>

               {action && <ModalActions className={classes.action}>{action}</ModalActions>}
            </div>
         </Draggable>
      </article>
   )
}
